import { CircleCheck, CircleX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DnsRecordWorkspaceConfigurationReadModel } from '@r/api/generated/client.schemas.ts';
import { CopyButton } from '@ui/copy-button.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/table.tsx';

// Nombre de caractères toujours visibles en fin de valeur, le début étant tronqué selon la largeur disponible
const VALUE_VISIBLE_END_LENGTH = 12;

interface DnsRecordsTableProps {
  records: DnsRecordWorkspaceConfigurationReadModel[];
}

/*
 * Valeur tronquée au milieu (le début et la fin restent lisibles), complète au survol et à la copie.
 */
const MiddleTruncatedValue = ({ value }: { value: string }) => {
  const splitIndex = Math.max(value.length - VALUE_VISIBLE_END_LENGTH, 0);

  return (
    <span className="flex items-center gap-3 min-w-0">
      <span className="flex min-w-0 font-mono text-xs" title={value}>
        <span className="truncate">{value.slice(0, splitIndex)}</span>
        <span className="shrink-0">{value.slice(splitIndex)}</span>
      </span>
      <CopyButton value={value} />
    </span>
  );
};

/*
 * Enregistrements DNS à ajouter dans la zone DNS du domaine, avec leur statut et de quoi les copier.
 */
export const DnsRecordsTable = ({ records }: DnsRecordsTableProps) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-md border">
      <Table className="table-fixed">
        <TableHeader className="bg-muted/50">
          <TableRow>
            <TableHead className="w-24">
              {t('workspace.configuration.record.column.type')}
            </TableHead>
            <TableHead className="w-1/4">
              {t('workspace.configuration.record.column.host')}
            </TableHead>
            <TableHead>{t('workspace.configuration.record.column.value')}</TableHead>
            <TableHead className="w-14">
              <span className="sr-only">{t('workspace.configuration.record.column.status')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record) => (
            <TableRow key={`${record.type}-${record.host}-${record.value}`}>
              <TableCell>
                <span className="px-1.5 py-0.5 font-mono text-xs rounded bg-muted">
                  {record.type}
                </span>
              </TableCell>
              <TableCell>
                <span className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-xs truncate" title={record.host}>
                    {record.host}
                  </span>
                  <CopyButton value={record.host} />
                </span>
              </TableCell>
              <TableCell>
                <MiddleTruncatedValue value={record.value} />
              </TableCell>
              <TableCell>
                {record.isValid ? (
                  <CircleCheck
                    className="w-4 h-4 text-emerald-600"
                    aria-label={t('workspace.configuration.status.authenticated')}
                  />
                ) : (
                  <CircleX
                    className="w-4 h-4 text-red-500"
                    aria-label={t('workspace.configuration.status.not.authenticated')}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
